/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { AccentPreference } from '@poweralpha/common';

/*
 * PowerAlpha v4 color tokens. Mirrors the Figma "V4 Design System" variable collections
 * (Primitives, Accent, Semantic (restructured), Component (restructured), Domain); DESIGN.md at
 * the repo root has the rules. Change a value here and in Figma in the same PR.
 */

/** Tier 1 · Primitives: raw values with no meaning. Only the tiers below may read them. */
const primitives = {
  neutral: {
    0: '#FFFFFF',
    25: '#F8FAFD',
    50: '#EEF2FA',
    100: '#DCE3F0',
    200: '#C3CCDD',
    300: '#9AA7C7',
    400: '#7C8CAB',
    500: '#5B6C91',
    600: '#55617D',
    700: '#33415F',
    800: '#22304D',
    850: '#17223A',
    900: '#111A2E',
    950: '#0B1222',
    975: '#050B1A',
  },
  blue: {
    50: '#F3F7FE',
    100: '#E3EEFE',
    200: '#C9DEFD',
    300: '#A7C9FE',
    400: '#7EAEFD',
    500: '#5191FA',
    600: '#3976DD',
    700: '#295EBC',
    800: '#1E4999',
    900: '#163877',
    950: '#0F2654',
  },
  sky: {
    50: '#F1F8FD',
    100: '#DDF0FF',
    200: '#BDE2FD',
    300: '#90CFFF',
    400: '#55B7FC',
    500: '#209CE7',
    600: '#0081C6',
    700: '#0269A2',
    800: '#005383',
    900: '#003F67',
    950: '#002B4A',
  },
  ocean: {
    50: '#EEF9FD',
    100: '#DCF1FA',
    200: '#BCE4F5',
    300: '#95D1EC',
    400: '#6CB9DB',
    500: '#479FC4',
    600: '#2985A9',
    700: '#156C8E',
    800: '#095673',
    900: '#074259',
    950: '#062D3E',
  },
  teal: {
    50: '#ECFAF8',
    100: '#D8F4EF',
    200: '#B2E9E2',
    300: '#82DAD0',
    400: '#4BC4B9',
    500: '#0EAAA0',
    600: '#0B8D85',
    700: '#0A726D',
    800: '#005B57',
    900: '#024643',
    950: '#00312E',
  },
  indigo: {
    50: '#F4F6FE',
    100: '#E7ECFF',
    200: '#D2DAFF',
    300: '#B8C3FE',
    400: '#98A6FF',
    500: '#7984FF',
    600: '#6165ED',
    700: '#4E4FCA',
    800: '#3D3CA5',
    900: '#2F2D80',
    950: '#201F5A',
  },
  violet: {
    50: '#F7F5FF',
    100: '#EDEAFF',
    200: '#DDD6FF',
    300: '#C9BDFE',
    400: '#B19CFF',
    500: '#9B76FE',
    600: '#8456E8',
    700: '#6D40C5',
    800: '#5730A1',
    900: '#43237D',
    950: '#2E1858',
  },
  magenta: {
    50: '#FEF3FE',
    100: '#FDE3FE',
    200: '#FBC8FE',
    300: '#F8A4FF',
    400: '#E97CF4',
    500: '#D158DF',
    600: '#B43BC3',
    700: '#9626A5',
    800: '#791886',
    900: '#5D1168',
    950: '#410C49',
  },
  gold: {
    50: '#FCF7E2',
    100: '#FCEEC4',
    200: '#FEDE8B',
    300: '#FEC938',
    400: '#F3B601',
    500: '#CB940C',
    600: '#A87603',
    700: '#895C00',
    800: '#6E4600',
    900: '#563500',
    950: '#3F2200',
  },
  orange: {
    50: '#FFF5EE',
    100: '#FEE8DB',
    200: '#FFD2B7',
    300: '#FFB488',
    400: '#FD8B44',
    500: '#E86900',
    600: '#C25503',
    700: '#A14201',
    800: '#833200',
    900: '#672300',
    950: '#4A1500',
  },
  green: {
    50: '#EEFBF0',
    100: '#DCF4E0',
    200: '#BBEBC4',
    300: '#92DCA3',
    400: '#67C681',
    500: '#3CAD62',
    600: '#139249',
    700: '#007839',
    800: '#005F2C',
    900: '#004920',
    950: '#003315',
  },
  red: {
    50: '#FFF4F3',
    100: '#FEE7E5',
    200: '#FECFCD',
    300: '#FFAFAC',
    400: '#FF8484',
    500: '#FA4B58',
    600: '#DB2940',
    700: '#B90D2F',
    800: '#960022',
    900: '#750019',
    950: '#520210',
  },
  alpha: {
    white: {
      2: 'rgba(255, 255, 255, 0.02)',
      4: 'rgba(255, 255, 255, 0.04)',
      6: 'rgba(255, 255, 255, 0.06)',
      8: 'rgba(255, 255, 255, 0.08)',
      10: 'rgba(255, 255, 255, 0.1)',
      12: 'rgba(255, 255, 255, 0.12)',
      16: 'rgba(255, 255, 255, 0.16)',
      30: 'rgba(255, 255, 255, 0.3)',
      50: 'rgba(255, 255, 255, 0.5)',
      72: 'rgba(255, 255, 255, 0.72)',
      80: 'rgba(255, 255, 255, 0.8)',
      88: 'rgba(255, 255, 255, 0.88)',
      95: 'rgba(255, 255, 255, 0.95)',
    },
    ink: {
      4: 'rgba(15, 23, 42, 0.04)',
      6: 'rgba(15, 23, 42, 0.06)',
      8: 'rgba(15, 23, 42, 0.08)',
      12: 'rgba(15, 23, 42, 0.12)',
      30: 'rgba(15, 23, 42, 0.3)',
      50: 'rgba(15, 23, 42, 0.5)',
      84: 'rgba(15, 23, 42, 0.84)',
    },
    black: { 28: 'rgba(0, 0, 0, 0.28)', 40: 'rgba(0, 0, 0, 0.4)', 75: 'rgba(0, 0, 0, 0.75)' },
    canvas: { 72: 'rgba(8, 13, 32, 0.72)' },
    shadow: { 28: 'rgba(30, 41, 110, 0.28)' },
  },
  /**
   * The categorical chart palette: soft, light steps shared by Light and Dark (the v4 cash flow
   * palette). Tuned as a set, not taken from the ramps.
   */
  dataviz: [
    '#5AB8FF', // 1 blue
    '#FB923C', // 2 orange
    '#2DD4BF', // 3 teal
    '#FB7185', // 4 rose
    '#C4B5FD', // 5 lavender
    '#FDE68A', // 6 pale yellow
    '#F9A8D4', // 7 pink
    '#86EFAC', // 8 green
    '#A78BFA', // 9 violet
    '#F7C738', // 10 gold
    '#7DD3FC', // 11 sky
    '#FDBA74', // 12 peach
    '#94A3B8', // 13 slate
  ],
} as const;

export type ColorMode = 'dark' | 'light';

/** `dataviz.categorical`, for module-level series maps: it is the same in both modes. */
export const categorical = primitives.dataviz;
type Ramp = keyof typeof primitives.blue;
type Family = Exclude<keyof typeof primitives, 'neutral' | 'alpha' | 'dataviz'>;

const p = primitives;

export interface RolePalette {
  main: string;
  light: string;
  dark: string;
  contrastText: string;
}

const role = (main: string, light: string, dark: string, contrastText: string): RolePalette => ({
  main,
  light,
  dark,
  contrastText,
});

/**
 * Tier 2 · Semantic · Palette: `primary` is the user's accent (Settings › Appearance › Accent
 * color). Light uses step 700 so white text passes 4.5:1; dark uses step 400 with dark text, and
 * step 500 for `dark` (the contained-button hover fill) so that dark text still passes 4.5:1.
 */
const accentFrom = (family: Family | 'neutral'): Record<ColorMode, RolePalette> =>
  family === 'neutral'
    ? {
        light: role(p.neutral[700], p.neutral[400], p.neutral[800], p.neutral[0]),
        dark: role(p.neutral[200], p.neutral[100], p.neutral[300], p.neutral[950]),
      }
    : {
        light: role(p[family][700], p[family][500], p[family][800], p.neutral[0]),
        dark: role(p[family][400], p[family][300], p[family][500], p.neutral[950]),
      };

export const accents = {
  sky: {
    light: role(p.blue[700], p.blue[500], p.blue[800], p.neutral[0]),
    dark: role(p.sky[400], p.sky[300], p.sky[500], p.neutral[950]),
  },
  indigo: accentFrom('indigo'),
  violet: accentFrom('violet'),
  magenta: accentFrom('magenta'),
  graphite: accentFrom('neutral'),
} satisfies Record<AccentPreference, Record<ColorMode, RolePalette>>;

/** Tier 2 · Semantic: Palette (roles MUI reads) and Usage (named by what it paints). */
export const semantic = {
  light: {
    secondary: role(p.gold[400], p.gold[200], p.gold[500], p.neutral[950]),
    info: role(p.ocean[700], p.ocean[400], p.ocean[800], p.neutral[0]),
    success: role(p.green[700], p.green[400], p.green[800], p.neutral[0]),
    warning: role(p.orange[500], p.orange[300], p.orange[600], p.neutral[950]),
    error: role(p.red[600], p.red[300], p.red[700], p.neutral[0]),
    background: {
      default: p.neutral[50],
      paper: p.neutral[0],
      raised: p.neutral[25],
      well: p.alpha.white[72],
      floating: p.alpha.white[88],
      subtle: {
        secondary: p.gold[50],
        info: p.ocean[50],
        success: p.green[50],
        warning: p.orange[50],
        error: p.red[50],
      },
    },
    text: {
      primary: p.neutral[900],
      secondary: p.neutral[600],
      brand: p.gold[700],
      info: p.ocean[700],
      success: p.green[700],
      warning: p.orange[700],
      error: p.red[700],
    },
    border: { default: p.neutral[100], strong: p.neutral[400], hairline: p.alpha.ink[8] },
    overlay: {
      hover: p.alpha.ink[4],
      pressed: p.alpha.ink[8],
      selected: p.alpha.ink[12],
      scrim: p.alpha.ink[50],
    },
    shadow: { contact: p.alpha.ink[6], ambient: p.alpha.shadow[28] },
    dataviz: {
      categorical: p.dataviz,
      sequential: [100, 300, 400, 500, 600, 700, 800, 900].map((s) => p.ocean[s as Ramp]),
      diverging: [
        p.ocean[800],
        p.ocean[600],
        p.ocean[300],
        p.neutral[100],
        p.orange[300],
        p.orange[500],
        p.orange[700],
      ],
      stage: [400, 500, 600, 700, 800, 900, 950].map((s) => p.ocean[s as Ramp]),
    },
  },
  dark: {
    secondary: role(p.gold[300], p.gold[200], p.gold[400], p.neutral[950]),
    info: role(p.ocean[400], p.ocean[300], p.ocean[600], p.neutral[950]),
    success: role(p.green[400], p.green[300], p.green[600], p.neutral[950]),
    warning: role(p.orange[400], p.orange[300], p.orange[500], p.neutral[950]),
    error: role(p.red[400], p.red[300], p.red[500], p.neutral[950]),
    background: {
      default: p.neutral[950],
      paper: p.neutral[900],
      raised: p.neutral[850],
      well: p.alpha.black[28],
      floating: p.alpha.canvas[72],
      subtle: {
        secondary: p.gold[950],
        info: p.ocean[950],
        success: p.green[950],
        warning: p.orange[950],
        error: p.red[950],
      },
    },
    text: {
      primary: p.neutral[50],
      secondary: p.neutral[300],
      brand: p.gold[300],
      info: p.ocean[300],
      success: p.green[300],
      warning: p.orange[300],
      error: p.red[300],
    },
    border: { default: p.neutral[800], strong: p.neutral[500], hairline: p.alpha.white[8] },
    overlay: {
      hover: p.alpha.white[6],
      pressed: p.alpha.white[10],
      selected: p.alpha.white[16],
      scrim: p.alpha.black[75],
    },
    shadow: { contact: p.alpha.black[40], ambient: p.alpha.black[75] },
    dataviz: {
      categorical: p.dataviz,
      sequential: [900, 800, 700, 600, 500, 400, 300, 100].map((s) => p.ocean[s as Ramp]),
      diverging: [
        p.ocean[300],
        p.ocean[500],
        p.ocean[700],
        p.neutral[800],
        p.orange[700],
        p.orange[500],
        p.orange[300],
      ],
      stage: [800, 700, 600, 500, 400, 300, 200].map((s) => p.ocean[s as Ramp]),
    },
  },
} as const;

/** Tier 3 · Component: tokens that belong to one component or feature. */
export const component = {
  light: {
    scrollbar: { thumb: p.neutral[200], thumbActive: p.neutral[400] },
    agent: {
      foreground: p.gold[300],
      background: p.gold[50],
      gradient: { start: p.sky[400], mid: p.indigo[400], end: p.violet[400] },
    },
    glass: {
      top: p.alpha.white[80],
      bottom: p.alpha.white[50],
      border: p.alpha.white[95],
      highlight: p.neutral[0],
    },
    desktop: {
      wallpaper: p.neutral[50],
      aurora: [p.blue[400], p.sky[400], p.violet[400], p.teal[400]],
    },
    diagram: {
      node: p.neutral[0],
      edge: p.neutral[400],
      group: p.neutral[50],
      lossLine: p.neutral[600],
    },
    /** Gain/loss bars (energy waterfalls, sensitivity tornado): one step lighter than `success`/`error.main`. */
    waterfall: { gain: p.green[500], loss: p.red[400] },
  },
  dark: {
    scrollbar: { thumb: p.neutral[700], thumbActive: p.neutral[300] },
    agent: {
      foreground: p.gold[300],
      background: p.gold[950],
      gradient: { start: p.sky[300], mid: p.indigo[300], end: p.violet[300] },
    },
    glass: {
      top: p.alpha.white[8],
      bottom: p.alpha.white[2],
      border: p.alpha.white[10],
      highlight: p.alpha.white[12],
    },
    desktop: {
      wallpaper: p.neutral[975],
      aurora: [p.blue[600], p.sky[600], p.violet[700], p.teal[600]],
    },
    diagram: {
      node: p.neutral[850],
      edge: p.neutral[500],
      group: p.neutral[800],
      lossLine: p.neutral[300],
    },
    waterfall: { gain: p.green[400], loss: p.red[400] },
  },
} as const;

export interface DomainColor {
  /** Marks, fills and dots: a categorical chart slot. */
  main: string;
  /** Labels; at least 4.5:1 on the tint and on paper. */
  text: string;
  /** Tag background. */
  tint: string;
  /** Tag border: a soft step of the same family as `text` and `tint`. */
  border: string;
}

/** Categorical slot (1-based, as in DESIGN.md) plus the family that supplies text, tint and border. */
const concept =
  (mode: ColorMode) =>
  (slot: number, family: Family): DomainColor => ({
    main: p.dataviz[slot - 1],
    text: p[family][mode === 'light' ? 700 : 300],
    tint: p[family][mode === 'light' ? 100 : 950],
    border: p[family][mode === 'light' ? 300 : 800],
  });

const domainFor = (mode: ColorMode) => {
  const c = concept(mode);
  return {
    tool: {
      ess: c(1, 'sky'),
      pvsim: c(4, 'gold'),
      pvcosting: c(6, 'green'),
      plantsim: c(7, 'indigo'),
      project: c(3, 'teal'),
    },
    designStage: { bod: c(4, 'gold'), por: c(1, 'sky'), abd: c(6, 'green') },
    technology: {
      storage: c(1, 'sky'),
      solar: c(4, 'gold'),
      wind: c(3, 'teal'),
      gas: c(2, 'orange'),
      hybrid: c(7, 'indigo'),
    },
  };
};

/** Beside Semantic · Domain: what a thing is. Never follows the accent. */
export const domain = { light: domainFor('light'), dark: domainFor('dark') } as const;

/** Literal token values widened to `string`, so both modes share one shape. */
type Widen<T> = T extends string
  ? string
  : T extends readonly (infer U)[]
    ? readonly Widen<U>[]
    : { readonly [K in keyof T]: Widen<T[K]> };

export type DomainTokens = Widen<ReturnType<typeof domainFor>>;
export type SemanticTokens = Widen<(typeof semantic)['light']>;
export type ComponentTokens = Widen<(typeof component)['light']>;

/**
 * Outside the tiers on purpose: third-party brand colors and GIS legends that must match their
 * source. Keep every other hardcoded color out of components.
 */
export const external = {
  brand: {
    pdf: '#D93025',
    word: '#2B579A',
    excel: '#217346',
    powerpoint: '#D24726',
    outlook: '#0078D4',
    salesforce: '#21A0E0',
  },
} as const;

/**
 * Every token for the theme's mode. Reads the static tokens rather than `theme.palette`, so it
 * also works under MUI's default theme (tests and stories without the app's ThemeProvider).
 */
export const getThemeTokens = (theme: { palette: { mode: ColorMode } }) => {
  const { mode } = theme.palette;
  return { ...semantic[mode], component: component[mode], domain: domain[mode] };
};

export { primitives };
